import { Box, Stack, Typography } from '@mui/material'
import { ChatBubbleLeftRightIcon, SparklesIcon } from '@heroicons/react/24/outline'

import type { CoachConversation } from '../../../../../../../pure-js/types/CoachTypes'
import { CoachConversationMode } from '../../../../../../../pure-js/types/CoachTypes'
import { stripMarkdownFormatting } from '../../../../../../../pure-js/libs/Common'
import { INTERAX_COLORS } from '../../../../../constants/colors'
import { useTranslation } from '../../../../../shared/hooks/useIntractionTranslate'

type CoachConversationListProps = {
  conversations: CoachConversation[]
  overallSummary?: string
  selectedId?: string
  /** Turn count of the conversation being spoken right now; the stored count lags by one flush. */
  liveMessageCount?: number
  /** Still fetching. Says nothing rather than asserting there is no history before it is known. */
  isLoading?: boolean
  onSelect: (id: string) => void
}

/** The history, and only the history — starting a new conversation belongs to the panel around it. */
export const CoachConversationList = ({
  conversations,
  overallSummary,
  selectedId,
  liveMessageCount,
  isLoading = false,
  onSelect
}: CoachConversationListProps) => {
  const { t } = useTranslation()

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center">
        <ChatBubbleLeftRightIcon width={20} color={INTERAX_COLORS.sun} />
        <Typography sx={{ color: INTERAX_COLORS.white, fontWeight: 700 }}>
          {t('gamer.ariaCoach.conversations')}
        </Typography>
      </Stack>

      {overallSummary && (
        <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: INTERAX_COLORS.sun20 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <SparklesIcon width={16} color={INTERAX_COLORS.sun} />
            <Typography sx={{ color: INTERAX_COLORS.sun, fontSize: 12, fontWeight: 700 }}>
              {t('gamer.ariaCoach.achievedTogether')}
            </Typography>
          </Stack>
          <Typography sx={{ color: INTERAX_COLORS.white, fontSize: 13 }}>
            {stripMarkdownFormatting(overallSummary)}
          </Typography>
        </Box>
      )}

      {/* Only once the answer is known, or the panel claims an empty history while it is still loading. */}
      {conversations.length === 0 && !isLoading && (
        <Typography sx={{ color: INTERAX_COLORS.thunderGrey, fontSize: 14 }}>
          {t('gamer.ariaCoach.noConversations')}
        </Typography>
      )}

      <Stack spacing={1}>
        {conversations.map((conversation) => {
          const isSelected = conversation.id === selectedId
          const modeLabel =
            conversation.mode === CoachConversationMode.VOICE
              ? t('gamer.ariaCoach.voiceMode')
              : t('gamer.ariaCoach.textMode')
          const messageCount =
            isSelected && liveMessageCount !== undefined ? liveMessageCount : (conversation.messageCount ?? 0)

          return (
            <Box
              key={conversation.id}
              role="button"
              tabIndex={0}
              onClick={() => onSelect(conversation.id)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return
                event.preventDefault()
                onSelect(conversation.id)
              }}
              sx={{
                p: 1.5,
                borderRadius: 2,
                cursor: 'pointer',
                border: `1px solid ${isSelected ? INTERAX_COLORS.sun : 'transparent'}`,
                backgroundColor: isSelected ? INTERAX_COLORS.sun20 : INTERAX_COLORS.black20
              }}
            >
              <Typography sx={{ color: INTERAX_COLORS.white, fontSize: 14, fontWeight: 600 }} noWrap>
                {conversation.title || t('gamer.ariaCoach.conversationFallbackTitle')}
              </Typography>
              {conversation.summary && (
                <Typography sx={{ color: INTERAX_COLORS.thunderGrey, fontSize: 12, mt: 0.5 }} noWrap>
                  {stripMarkdownFormatting(conversation.summary)}
                </Typography>
              )}
              <Typography sx={{ color: INTERAX_COLORS.thunderGrey, fontSize: 11, mt: 0.5 }}>
                {modeLabel} · {t('gamer.ariaCoach.messagesCount', { count: messageCount })}
              </Typography>
            </Box>
          )
        })}
      </Stack>
    </Stack>
  )
}
